import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Group,
	NumberInput,
	Stack,
	Switch,
	Textarea,
} from "@mantine/core";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	type ReferralProgramValues,
	referralProgramSchema,
} from "@/features/referral/lib/referral-schemas";

export function ReferralProgramForm({
	defaults,
	saving,
	onSave,
}: {
	defaults: ReferralProgramValues;
	saving: boolean;
	onSave: (values: ReferralProgramValues) => void;
}) {
	const { t } = useTranslation();
	const {
		control,
		handleSubmit,
		reset,
		trigger,
		formState: { isDirty, isValid },
	} = useForm<ReferralProgramValues>({
		resolver: zodResolver(referralProgramSchema),
		defaultValues: defaults,
		mode: "onChange",
	});

	useEffect(() => {
		reset({
			isActive: defaults.isActive,
			pointsPerReferral: defaults.pointsPerReferral,
			shareText: defaults.shareText,
		});
		void trigger();
	}, [
		defaults.isActive,
		defaults.pointsPerReferral,
		defaults.shareText,
		reset,
		trigger,
	]);

	return (
		<form onSubmit={handleSubmit(onSave)}>
			<Stack>
				<Controller
					name="isActive"
					control={control}
					render={({ field }) => (
						<Switch
							label={t("referral.programActive")}
							description={t("referral.programActiveHint")}
							checked={field.value}
							onChange={(event) => field.onChange(event.currentTarget.checked)}
						/>
					)}
				/>
				<Controller
					name="pointsPerReferral"
					control={control}
					render={({ field, fieldState }) => (
						<NumberInput
							label={t("referral.pointsPerReferral")}
							min={1}
							value={field.value}
							onChange={field.onChange}
							error={fieldState.error?.message}
						/>
					)}
				/>
				<Controller
					name="shareText"
					control={control}
					render={({ field }) => (
						<Textarea
							{...field}
							label={t("referral.shareText")}
							description={t("referral.shareTextHint")}
							minRows={3}
						/>
					)}
				/>
				<Group justify="flex-end">
					<Button
						type="submit"
						loading={saving}
						disabled={!isDirty || !isValid}
					>
						{t("common.save")}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
